import Vue from 'vue'
import VueResource from 'vue-resource'
import { setAuthorizationHeader, retryAfterAuthFail } from './auth'
import track, { isResponse } from 'js/helpers/analytics'

Vue.use(VueResource)

Vue.http.options.root = '/api/v5'
// Vue.http.options.crossOrigin = true
// Vue.http.options.credentials = true


/**
 * Track requests & response
 */
Vue.http.interceptors.push( (request, next) => {

    const currentTime = () => window.performance ? window.performance.now() : Date.now()

    console.log('----> API REQUEST', request);
    const start = currentTime()

    next( response => {

        const duration = Math.floor(currentTime() - start)
        console.log('<---- API RESPONSE', response, duration);

        if ( response.status < 400 ) {

            track.event('api', response.status, response.url, duration)

        } else if ( response.status === 401 ) {

            track.event('api', 'unauthenticated', response.url, duration)

        } else if ( response.status === 403 ) {

            track.event('api', 'unauthorized', response.url, duration)

        } else if ( response.status === 409 ) {

            track.event('api', 'conflict', response.url, duration)

        } else if ( response.status === 410 ) {

            track.event('api', 'update', Vue.http.options.root, duration)

        } else if ( response.status === 422 ) {

            track.event('api', 'invalid', response.url, duration)

        } else if ( response.status === 429 ) {

            track.event('api', 'throttle', response.url, duration)

        } else if ( response.status === 503 ) {

            track.event('api', 'maintenance', response.url, duration)

        } else if ( response.status >= 400 ) {

            track.exception('API response error', response)

        }

    })

})

/**
 * Handles new API versions
 */
Vue.http.interceptors.push( (request, next) => {

    next( response => {

        if ( response.status === 410 ) {
            setTimeout( () => {
                window.location.reload(true)
            }, 3000)
        }

    })
})


/**
 * Handles authentication
 */
Vue.http.interceptors.push( (request, next) => {

    setAuthorizationHeader(request)

    next( response => {

        const authError = !response.ok && (response.status === 401 )
        const retriesLeft = (request.retry === undefined || request.retry > 0)

        if (authError && retriesLeft) {
            // try to authenticate first, then retry original request. If anything fails, return the new response or original failed response
            return retryAfterAuthFail(request).catch( err => isResponse(err) ? err : response )
        }

    });

});

/**
 * Fail requests that take too long
 * Returns response with status code 1
 */
Vue.http.interceptors.push( (request, next) => {

    const timeout = setTimeout(() => {
        request.abort();
        const response = request.respondWith('', {status: 1, statusText: 'Timeout'});
        next(response)
    }, 15000);

    next( () => {
        clearTimeout(timeout);
    })

})


export default {
    getTerm: (id, includeProgress) => Vue.http.get('groupterms/' + id, {params: { includeProgress: includeProgress }}),
    getTermForPeriod: (id, includeProgress) => Vue.http.get('groupperiods/' + id + '/groupterms', {params: { includeProgress: includeProgress }}),
    getTerms: () => Vue.http.get('terms'),
    getTermsForYear: (yearId) => Vue.http.get('years/' + yearId + '/terms'),
    getTermsForLevel: (levelId) => Vue.http.get('levels/' + levelId + '/terms'),
    getAssignment: (id, includeProgress) => Vue.http.get('assignments/' + id, {params: { includeProgress: includeProgress, include: 'essentials' }}),
    getAssignmentSequence: (id) => Vue.http.get('assignments/' + id, {params: { include: 'predecessors,successors' }}),
    getUser: (id, includeEssentials) => Vue.http.get('users/' + id, {params: { include: includeEssentials ? 'essentials' : null }} ),
    getDefinition: (tag) => Vue.http.get('definitions/' + tag),
    getTeachertips: (stepId) => Vue.http.get('steps/' + stepId + '/teachertips'),
    getReflectiontips: (assignmentId) => Vue.http.get('assignments/' + assignmentId + '/reflectiontips'),

    // progress
    startedAssignment: (assignmentId) => Vue.http.post('progress', { assignment_id: assignmentId }),
    viewedStep: (progressId, stepIndex) => Vue.http.patch('progress/'+progressId+'/viewed', { step_index: stepIndex }),
    submittedStep: (progressId, stepIndex) => Vue.http.patch('progress/'+progressId+'/submitted', { step_index: stepIndex }),
    finishedAssignment: (progressId) => Vue.http.patch('progress/'+progressId+'/finished'),
    patchDiligence: (progressId, diligence) => Vue.http.patch('progress/'+progressId+'/diligence', { diligence }),
    patchBadge: (progressId, badge) => Vue.http.patch('progress/'+progressId+'/badge', { badge }),
    getProgressForGroup: (groupId, includeFeedback) => Vue.http.get('groups/'+groupId+'/progress', { params: { include: includeFeedback ? 'feedback' : null }}),
    retakeProgress: (progressId) => Vue.http.patch('progress/'+progressId, { isRetaking: true }),
    reviewedProgress: (progressId) => Vue.http.patch('progress/'+progressId, { isReviewed: true }),

    giveFeedback: (assignmentId, feedback) => Vue.http.patch('assignments/'+assignmentId+'/feedback', feedback),

    // submissions
    addSubmission: (submission, partialId) => Vue.http.post('partials/'+partialId+'/submissions', { payload: submission }),
    patchSubmission: (submission) => Vue.http.patch('submissions/'+submission.id, submission),
    deleteSubmission: (submissionId) => Vue.http.delete('submissions/'+submissionId),
    getReflectionSubmission: (progressId) => Vue.http.get('progress/'+progressId+'/reflectables'),
    getFallbackReflectionSubmission: (assignmentId) => Vue.http.get('assignments/'+assignmentId+'/reflectables'),
    getGroupSubmissions: (groupId, assignmentId, limit) => Vue.http.get('groups/'+groupId+'/assignments/'+assignmentId+'/submissions', {params: { limit: limit }}),
    getReflections: (progressId) => Vue.http.get('progress/'+progressId + '/reflections'),

    // auth
    getAccessToken: (token, stealth) => Vue.http.get('auth/refresh', { headers: { Authorization: 'Bearer ' + token}, stealth: stealth, retry: 1}),
    login: (email, password, remember) => Vue.http.post('auth/login', { email: email, password: password, remember: remember ? 'yes' : 'no' }, { retry: 1 }),
    logout: (refreshToken) => Vue.http.post('auth/logout', { refreshtoken: refreshToken }, { retry: 1 }),
    registrationRequest: (email, code, terms) => Vue.http.post('registration', { email, code, terms }),
    registrationVerify: (token) => Vue.http.post('registration/verify', { token }),
    register: (firstname, infix, lastname, password, passwordConfirmation, token) => Vue.http.post('users', {firstname, middlename: infix, lastname, password, password_confirmation: passwordConfirmation, token }),
    forgotPassword: (email) => Vue.http.post('auth/forgotpassword', { email }),
    resetPassword: (token, email, password, passwordConfirmation) => Vue.http.post('auth/resetpassword', { token, email, password, password_confirmation: passwordConfirmation }),
    editUser: (id, firstname, infix, lastname) => Vue.http.patch('users/' + id, { firstname, middlename: infix, lastname }),
    editUserEmail: (id, email, password) => Vue.http.patch('users/' + id, { email, password }),
    editUserPassword: (id, oldPassword, newPassword, newPasswordConfirmation) => Vue.http.patch('users/' + id , { password: oldPassword, newPassword: newPassword, newPassword_confirmation: newPasswordConfirmation }),
    confirmUserEmail: (token) =>  Vue.http.patch('users/email', { token }),

    getGroupsForTeacher: (teacherId) => Vue.http.get('teachers/'+teacherId+'/groups'),
    getGroupsForSchool: (schoolId) => Vue.http.get('schools/'+schoolId+'/groups'),
    addGroupToTeacher: (teacherId, groupId, includeTerms) => Vue.http.post('teachers/'+teacherId+'/groups', { group_id: groupId }, { params: { include: includeTerms ? 'term.period' : null }}),
    removeGroupFromTeacher: (teacherId, groupId) => Vue.http.delete('teachers/'+teacherId+'/groups', { body: { group_id: groupId } }),
    getStudentsForGroup: (groupId) => Vue.http.get('groups/'+ groupId +'/students'),

    getPlanning: (groupId) => Vue.http.get('groups/' +groupId+'/planning'),
    patchPlanning: (groupId, data) => Vue.http.patch('groups/'+groupId+'/planning', { planning: data }),

    patchAssignmentsForGroup: (groupId, optionalAssignments) => Vue.http.patch('groups/'+groupId+'/assignments', { optionalAssignments }),

    addFeedback: (progressId, feedback, compliments, remarks) => Vue.http.post('progress/'+progressId+'/feedback', { comments: feedback, compliments, remarks }),
    patchFeedback: (feedbackId, feedback, compliments, remarks) => Vue.http.patch('feedback/'+feedbackId, { comments: feedback, compliments, remarks }),
    getFeedback: (progressId) => Vue.http.get('progress/'+progressId+'/feedback'),

    getHolidays: (schoolId) => Vue.http.get('schools/'+schoolId+'/holidays'),
    getLevelsForSchool: (schoolId) => Vue.http.get('schools/'+schoolId+'/levels'),
    getLevelsForGroup: (groupId) => Vue.http.get('groups/'+groupId+'/levels'),
    getLevels: () => Vue.http.get('levels'),
    getYears: () => Vue.http.get('years'),

    getGroup: (groupId) => Vue.http.get('groups/'+groupId),
    addGroup: (schoolId, name, level) => Vue.http.post('schools/'+schoolId+'/groups', { name, level }),
    patchGroup: (groupId, data) => Vue.http.patch('groups/'+groupId, data ),

    moveToGroup: (userId, code) => Vue.http.patch('users/'+userId+'/group', { code }),


    // admin
    getMails: () => Vue.http.get('mails'),
    getMail: (id) => Vue.http.get('mails/'+id),
    addMail: (data) => Vue.http.post('mails', data),
    patchMail: (id, data) => Vue.http.patch('mails/'+id, data),
    testMail: (id) => Vue.http.get('mails/'+id+'/test'),

    getSubscribers: () => Vue.http.get('subscribers'),
    addSubscriber: (data) => Vue.http.post('subscribers', data),
    deleteSubscriber: (id) => Vue.http.delete('subscribers/'+id),

    getArticles: () => Vue.http.get('articles'),
    getArticle: (id) => Vue.http.get('articles/'+id),
    addArticle: (data) => Vue.http.post('articles', data),
    patchArticle: (id, data) => Vue.http.patch('articles/'+id, data),
    deleteArticle: (id) => Vue.http.delete('articles/'+id),

    getSchools: (includeMeta) => Vue.http.get('schools', { params: { include: includeMeta ? 'meta' : null } }),
    getSchool: (id, includeTickets) => Vue.http.get('schools/' + id, { params: { include: includeTickets ? 'tickets' : null }}),
    addSchool: (data) => Vue.http.post('schools', data),
    patchSchool: (id, data) => Vue.http.patch('schools/' + id, data),
    sendSchoolInvitation: (id, type) => Vue.http.post('schools/' + id + '/invitation', { type }),

    reloadSchoolTickets: (schoolId, type, amount) => Vue.http.patch('schools/' + schoolId + '/tickets/' + type, { amount }),
    deleteSchoolTicket: (schoolTicketId) => Vue.http.delete('schooltickets/' + schoolTicketId),
    getTickets: () => Vue.http.get('tickets'),

    deleteImage: (path) => Vue.http.delete('images/'+path),

    getTicketActivitiesForSchool: (schoolId) => Vue.http.get('activities/subject/schools/'+schoolId+'/tickets'),
    getNotificationActivitiesForSchool: (schoolId) => Vue.http.get('activities/subject/schools/'+schoolId+'/notifications'),


    getOrders: () => Vue.http.get('activationorders'),
    addOrder: (data) => Vue.http.post('activationorders', data),
    getCodesForOrder: (orderId) => Vue.http.get('activationorders/'+orderId+'/codes'),

    getLegalTerms: () => Vue.http.get('/voorwaarden?raw=true'),
    getLegalPrivacy: () => Vue.http.get('/privacy?raw=true'),
    getLegalDisclaimer: () => Vue.http.get('/disclaimer?raw=true'),

}



// WEBPACK FOOTER //
// ./resources/assets/js/api/resources.js